/**
 * @author       Matthieu Devlin (matt@zumper.com)
 * @copyright    Copyright (c) 2023, Zumper
 * @description  initializeOptimizely.ts
 */

import { localStorageSingleton } from '@zcode/nr-utils'
import { type OptimizelyClient, createInstance, setLogLevel } from '@zcode/optimizely-client'
import { logger } from '../logger'

setLogLevel('warn')

// NOTE: OPT_FORMAT_RE and parseOptQuery are copied from getExperimentOverrides in packages/express-optimizely
// Comma separated experiment_name:variation_name string
const OPT_FORMAT_RE = /^[\w-]+:[\w-]+(?:,[\w-]+:[\w-]+)*$/

const parseOptQuery = (opt: string): Record<string, string> => {
  if (!opt.match(OPT_FORMAT_RE)) {
    logger.error(
      `opt query param does not match accepted format of comma separated experiment_name:variation_name string, opt=${opt}`
    )
    return {}
  }
  return Object.fromEntries(opt.split(',').map((value) => value.split(':')))
}
export interface OptimizelyConfig {
  datafile: string
  user: OptimizelyClient['user']
  serverRenderedExperiments: string[]
  variationMap: Record<string, string>
}

const UPS_LS_KEY = 'optimizely-ups-data'

const localStorageAdapter = {
  read: () => {
    const UPSDataObject = localStorageSingleton.get(UPS_LS_KEY) || {}
    return UPSDataObject
  },
  write: (data: {
    user_id: {
      [experiment_id: string]: {
        variation_id: string
      }
    }
  }) => {
    localStorageSingleton.set(UPS_LS_KEY, data)
  },
}

export class UserProfileService {
  constructor(public readonly isActivatedExperimentId: (id: string) => boolean) {}

  lookup(userId: string) {
    return localStorageAdapter.read()[userId]
  }

  save(
    userProfileMap: {
      user_id: string
      experiment_bucket_map: {
        [experiment_id: string]: {
          variation_id: string
        }
      }
    },
    skipActivatedCheck = false
  ) {
    const overwriteData = localStorageAdapter.read()
    overwriteData[userProfileMap.user_id] = {
      ...overwriteData[userProfileMap.user_id],
      ...(skipActivatedCheck
        ? userProfileMap.experiment_bucket_map
        : // Optimizely wants to add every experiment that we call `getVariation` or
          // `activate` for to this list
          // But we only want this list to include activated experiments, so we filter to just those
          Object.fromEntries(
            Object.entries(userProfileMap.experiment_bucket_map).filter(([experimentId]) =>
              this.isActivatedExperimentId(experimentId)
            )
          )),
    }
    localStorageAdapter.write(overwriteData)
  }
}

export const initializeOptimizely = (
  config: OptimizelyConfig,
  location: Pick<Location, 'search'>
): OptimizelyClient => {
  const activatedExperiments = new Set<string>()
  const isActivatedExperimentId = (experimentId: string) => {
    const config = client.getOptimizelyConfig()
    if (!config?.experimentsMap) {
      return false
    }
    for (const experimentName of activatedExperiments) {
      if (config.experimentsMap[experimentName]?.id === experimentId) {
        return true
      }
    }
    return false
  }
  const client = createInstance(
    {
      datafile: config.datafile,
      userProfileService: new UserProfileService(isActivatedExperimentId),
      // set the default eventBatchSize and eventFlushInterval so we don't get warnings in the logs
      // see https://docs.developers.optimizely.com/full-stack-experimentation/docs/event-batching-react#configure-event-batching
      eventBatchSize: 10,
      eventFlushInterval: 1000,
    },
    new Map(Object.entries(config.variationMap)),
    activatedExperiments
  )
  client.setUser(config.user)
  const optimizelyUser = config.user

  if (!optimizelyUser.id) {
    logger.warn('No ID for optimizely user, skipping setForcedVariation and activation')
    return client
  }

  const userId = optimizelyUser.id

  const optimizelyOverrideQueryString = new URLSearchParams(location.search).get('opt')
  if (optimizelyOverrideQueryString) {
    const optimizelyOverrides = parseOptQuery(optimizelyOverrideQueryString)
    for (const value of Object.entries(optimizelyOverrides)) {
      const [experiment, variation] = value
      client.setForcedVariation(experiment, userId, variation)
    }
  }

  // Only using the variation map to confirm that activated experiments match variations in fastly
  // Once we confirm over many requests - we can delete this check and the variation map
  for (const experiment of config.serverRenderedExperiments) {
    const activatedVariation = client.activate(experiment, userId)
    const expectedVariation = config.variationMap[experiment]
    if (activatedVariation !== expectedVariation) {
      logger.error(
        'Optimizely variation returned by activate is different from variation map,',
        `experiment=${experiment}, activated=${activatedVariation}, variationMap=${expectedVariation}`
      )
    }
  }
  return client
}
